import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { InvestigationAdvertisement } from "../components/InvestigationAdvertisement";
import { InvestigationSectionHeader } from "../components/InvestigationSectionHeader";
import { AddInterview } from "./AddInterview/AddInterview";
import { InterviewGeneralInfo } from "./InterviewGeneralInfo/InterviewGeneralInfo";
import { Separator } from "../components";
import { useConstants } from "../Main/useConstants";
import { useReport } from "../../../../../context/report/ReportContext";
import { EGIconButton } from "../../../../MainComponents/Button/IconButton/IconButton";
import { EGButton } from "../../../../MainComponents/Button/EGButton/EGButton";
import { SortMenuInterviews } from "./SortMenuInterviews";

export const Interviews = ({
  alias,
  interviews = [],
  setInterviews,
  justView = false,
  summaryMode = false,
  openConfirmDeleteModal,
  handleSelectedSection,
  closeModalConfirm,
  onReload,
  updateModalConfirm,
}) => {
  //---> Custom Hooks <---//
  const [t] = useTranslation("investigation");
  const {
    enums,
    selects: { typeUserLabels },
    functions: { getUserLabel },
  } = useConstants();
  const { state: reportState } = useReport();

  const refsMap = useRef({});

  // Content of interview to be edited (se declara aquí porque interviewedOpts
  // necesita conocer la entrevista en edición).
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [selectedInterviewID, setSelectedInterviewID] = useState(null);

  // Helper for reading formatted arrays from reportState
  const getFromArray = (path) => {
    const keys = path.split(".");
    let node = reportState;

    for (const key of keys) {
      if (node == null) return [];
      node = node[key];
    }

    if (!node) return [];
    if (Array.isArray(node)) return node;
    if (Array.isArray(node?.formatted)) return node.formatted;
    if (Array.isArray(node?.raw)) return node.raw;
    return [];
  };
  // Id del entrevistado de la entrevista en edición: no debe bloquearse a sí
  // mismo para que siga siendo seleccionable al editar.
  const editingInterviewedId = selectedInterview?.interviewed?.id ?? null;

  // Ids de involucrados que YA tienen una entrevista registrada. Se usan para
  // deshabilitarlos en el selector y evitar entrevistar dos veces a la misma
  // persona (la API de IA tomaría la primera entrevista coincidente).
  const interviewedIds = useMemo(
    () =>
      new Set(
        (interviews || [])
          .map((iv) => iv?.interviewed?.id)
          .filter((personId) => personId && personId !== editingInterviewedId),
      ),
    [interviews, editingInterviewedId],
  );

  // Formatted options from reportState
  const interviewedOpts = useMemo(() => {
    const {
      COMPLAINANT,
      INVOLVEDS_ACCOMPLICE,
      INVOLVEDS_OFFENDER,
      INVOLVEDS_VICTIM,
      INVOLVEDS_WITNESS,
    } = enums.typeUser;

    const usersWanted = [
      COMPLAINANT,
      INVOLVEDS_VICTIM,
      INVOLVEDS_ACCOMPLICE,
      INVOLVEDS_OFFENDER,
      INVOLVEDS_WITNESS,
    ];

    return usersWanted.map((value) => {
      if (value === COMPLAINANT) {
        const options = [
          {
            value: COMPLAINANT,
            label: t("Interview.complainant"),
            disabled: interviewedIds.has(COMPLAINANT),
          },
        ];
        return {
          label: "",
          key: value,
          options,
          hasOptions: true,
          // hasSelectable: queda al menos un involucrado sin entrevista previa
          hasSelectable: options.some((opt) => !opt.disabled),
        };
      }
      const fromArray = getFromArray(enums.typeUserRoutes[value]) || [];
      const options = fromArray.map((opt) => ({
        ...opt,
        disabled: opt.disabled || interviewedIds.has(opt.value),
      }));
      return {
        label: typeUserLabels[value],
        key: value,
        options,
        hasOptions: fromArray.length > 0,
        hasSelectable: options.some((opt) => !opt.disabled),
      };
    });
  }, [reportState, typeUserLabels, t, interviewedIds]);

  //---> Initial States <---//
  // Interview content
  const someInterviewExist = interviews?.length > 0;
  // Is adding interview?
  const [isAdding, setIsAdding] = useState(false);
  // No involved info (Modal)
  const [showNoInvolved, setShowNoInvolved] = useState(false);
  // Interview loading
  const [isLoading, setIsLoading] = useState(false);
  const [expandedID, setExpandedID] = useState(null);
  // This map will save { id: HTML reference }
  const registerRef = (id, el) => {
    refsMap.current[id] = el;
  };
  // open/close more info
  const handleToggle = (id) => {
    setExpandedID((prev) => (prev === id ? null : id));
  };
  // The child will use this to register its root element.
  const handleChildOpened = (id) => {
    //Calculation of the screen positioning of elements
    const el = refsMap.current[id];
    if (!el) return;

    const headerOffset = 105;
    const extraPadding = 20;

    const elementY = el.getBoundingClientRect().top + window.pageYOffset;

    const targetY = elementY - headerOffset - extraPadding;

    window.scrollTo({
      top: targetY,
      behavior: "smooth",
    });
  };

  // Add involved onClick
  const handleAddInterviewClick = () => {
    // Sólo se permite agregar si queda algún involucrado sin entrevistar
    const hasSelectable = interviewedOpts.some((opt) => opt.hasSelectable);
    if (hasSelectable) {
      setIsAdding(true);
    } else {
      setShowNoInvolved(true);
    }
  };
  // Edit
  const hdlEditInterview = (interview, id) => {
    setSelectedInterview(interview);
    setSelectedInterviewID(id);
    setIsAdding(true);
  };
  // Cancel editing
  const cancelEditInterview = () => {
    setSelectedInterview(null);
    setSelectedInterviewID(null);
    setIsAdding(false);
  };

  return (
    <Box sx={{ bgcolor: "background.paper" }} p={1}>
      {!justView && !isAdding && (
        <InvestigationSectionHeader
          text={t("Interview.include_interviews_report_investigation")}
          buttonText={t("Interview.add_interview")}
          buttonIcon="outline_messages_3"
          onClick={handleAddInterviewClick}
          extraButtons={[
            <SortMenuInterviews
              key="sort-interviews"
              interviews={interviews}
              onSorted={(sortedList) => setInterviews(sortedList)}
              getUserLabel={getUserLabel}
            />,
          ]}
        />
      )}

      {justView && (
        <Separator
          text={t("Entrevistas")}
          icon="outline_messages_3"
          iconButton={handleSelectedSection ? "outline_edit" : undefined}
          onClick={
            handleSelectedSection
              ? () => handleSelectedSection(enums.sections.interviews)
              : undefined
          }
          sx={{ marginBottom: 1.5 }}
        />
      )}

      {!isAdding && !someInterviewExist && (
        <InvestigationAdvertisement
          title={t("Interview.no_interviews_on_record")}
          description={t(
            "Interview.in_this_section_can_create_record_evaluate_interviews",
          )}
        />
      )}

      {!isAdding && someInterviewExist && (
        <Box
          sx={{
            mt: 1,
            display: "grid",
            gap: 2,
            p: 1.5,
            gridTemplateColumns: {
              xs: "1fr",
              md: `repeat(${Math.min(interviews.length, 2)}, 1fr)`,
            },
            justifyContent: "center",
            alignItems: "center",
            gridAutoRows: "min-content",
          }}
        >
          {interviews.map((interview) => (
            <InterviewGeneralInfo
              key={interview.id}
              id={interview.id}
              interview={interview}
              hdlEditInterview={hdlEditInterview}
              setInterviews={setInterviews}
              alias={alias ?? ""}
              justView={justView}
              openConfirmDeleteModal={openConfirmDeleteModal}
              closeModalConfirm={closeModalConfirm}
              onReload={onReload}
              updateModalConfirm={updateModalConfirm}
              expanded={expandedID === interview.id}
              onToggle={() => handleToggle(interview.id)}
              registerRef={registerRef}
              onOpened={handleChildOpened}
              summaryMode={summaryMode}
            />
          ))}
        </Box>
      )}

      {!justView && isAdding && (
        <AddInterview
          id={selectedInterviewID}
          alias={alias}
          selectedInterview={selectedInterview}
          interviewedOpts={interviewedOpts}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          setInterviews={setInterviews}
          onCancel={cancelEditInterview}
        />
      )}

      {/* Show no involved modal */}
      <Dialog
        open={showNoInvolved}
        onClose={() => setShowNoInvolved(false)}
        fullWidth
        maxWidth="sm"
      >
        <EGIconButton
          iconName="cancel_circle"
          iconColor="primary"
          sx={{ position: "absolute", top: 10, right: 10 }}
          onClick={() => setShowNoInvolved(false)}
        />
        <DialogTitle>
          <Typography
            variant="h4"
            component="span"
            color="secondary"
            fontWeight={600}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            {t("Interview.no_involveds_available")}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t("Interview.no_more_involveds_to_interview")}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ pb: 1.5, px: 2, gap: 1 }}>
          <EGButton
            onClick={() => setShowNoInvolved(false)}
            label={t("Interview.cancel")}
            color="secondary"
          />
          <EGButton
            onClick={() => {
              setShowNoInvolved(false);
              handleSelectedSection(enums.sections.involveds);
            }}
            label={t("Interview.go_to_involveds")}
            color="pink"
            labelColorHover="white"
            variant="outlined"
            iconColor="pink"
            iconColorHover="white"
            iconName="bold_profile_2_ser"
            iconPosition="end"
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
};
